import React, { useState, useEffect, useMemo, useRef } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { useComparison } from "../../contexts/ComparisonContext";
import getProductImageUrl from "../../utils/imageUtils";
import api from "../../utils/api/axiosInstance";
import { fetchCategoryDetails } from "../../hooks/useCategoryDetails";
import { useToast } from "../../contexts/ToastContext";
import PageSeo from "../../components/common/PageSeo";
import styles from "../../styles/pages/compare/ComparisonPage.module.css";
import {
  FaTimes,
  FaStar,
  FaCheckCircle,
  FaMinusCircle,
  FaTrophy,
  FaArrowLeft,
  FaChevronRight,
} from "react-icons/fa";
import capitalizeFirst from "../../utils/capitalizeFirst";
import AppFeedbackPrompt from "../../components/feedback/AppFeedbackPrompt";
import ComparisonAiSummary from "../../components/compare/ComparisonAiSummary";
import ComparisonAskChat from "../../components/compare/ComparisonAskChat";
import { readHomeChatMatchSessionId } from "../../utils/homeChatMatchPersistence";
import useHomeChatMatchSession from "../../hooks/useHomeChatMatchSession";
import { useUserAuth } from "../../contexts/UserAuthContext";
import { getSpecsMap } from "../../utils/productSpecs";
import {
  computeFieldComparisons,
  formatCompareFieldValue,
  isComparisonBest,
  isComparisonWorst,
  parseNumericValue,
} from "../../utils/compareFieldUtils";
import {
  extractCssColors,
  renderSpecValue,
} from "../product/helpers/productDetailViewHelpers";
import { getMatchPercent, formatFitScoreBadge } from "../../utils/matchScoreUtils";
import { getComparisonPicks, buildQuickComparisonTable, getDefaultCategories } from "../../utils/quickComparisonUtils";
import { resolveSearchParamsForProducts } from "../../utils/resolveComparisonAiSession";
import * as FaIcons from "react-icons/fa";
import * as AiIcons from "react-icons/ai";
import * as BsIcons from "react-icons/bs";

const ComparisonPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useUserAuth();
  const aiSession = useHomeChatMatchSession(user);
  const { selectedProducts, removeProduct, clearProducts, addProduct } =
    useComparison();
  const { showToast } = useToast();
  const [categoryFields, setCategoryFields] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [compareChatExpanded, setCompareChatExpanded] = useState(false);
  const [compareChatPrefill, setCompareChatPrefill] = useState(null);
  const lastProcessedSource = useRef(null);

  // Helper function to fetch full product details
  const mergeMatchMetadata = (original, fetched) => {
    if (!fetched) return original;
    return {
      ...fetched,
      matchScore: fetched.matchScore ?? original?.matchScore ?? original?.score,
      rank: fetched.rank ?? original?.rank,
      matchExplanation:
        original?.matchExplanation ?? fetched.matchExplanation,
    };
  };

  const fetchFullProductDetails = async (products) => {
    try {
      const productPromises = products.map((product) => {
        const productId = product._id || product;
        // Check if product has full specification data
        // Products from ProductCard might only have basic fields
        const specsMap = getSpecsMap(product);
        const hasFullData = specsMap && Object.keys(specsMap).length > 0;

        if (hasFullData) {
          // Product already has full data, return as-is
          return Promise.resolve(product);
        }
        // Fetch full product details from API
        return api
          .get(`/products/${productId}`)
          .then((res) => mergeMatchMetadata(product, res.data))
          .catch((err) => {
            console.error(`Failed to fetch product ${productId}:`, err);
            return product; // Fallback to original if fetch fails
          });
      });
      return await Promise.all(productPromises);
    } catch (err) {
      console.error("Error fetching full product details:", err);
      return products; // Return original products if fetch fails
    }
  };

  // Get products from location state or context
  useEffect(() => {
    const loadProducts = async () => {
      const currentSourceKey = location.state?.products
        ? `state-${JSON.stringify(
            location.state.products.map((p) => p._id || p),
          )}`
        : null;

      // First priority: products from location state
      if (location.state?.products && location.state.products.length > 0) {
        // Skip if we've already processed this source
        if (lastProcessedSource.current === currentSourceKey) {
          return;
        }

        setLoading(true);
        setLoadError(null);
        try {
          // Fetch full product details to ensure we have all specifications
          const productsToCompare = await fetchFullProductDetails(
            location.state.products,
          );
          setProducts(productsToCompare);
          lastProcessedSource.current = currentSourceKey;

          // Fetch category fields
          const categoryId =
            productsToCompare[0]?.category?._id ||
            productsToCompare[0]?.category ||
            (typeof productsToCompare[0]?.category === "object"
              ? productsToCompare[0]?.category?._id
              : null);

          if (categoryId) {
            try {
              const categoryDetails = await fetchCategoryDetails(categoryId);
              setCategoryFields(
                categoryDetails.allFields || categoryDetails.fields || [],
              );
            } catch (err) {
              console.error("Failed to fetch category fields:", err);
            }
          }
        } catch (err) {
          console.error("Error loading products from state:", err);
          showToast("Failed to load products", "error");
          setLoadError("We couldn't load these products. Please try again.");
        } finally {
          setLoading(false);
        }
        return;
      }

      // Second priority: products from context (only if no external source)
      if (
        !currentSourceKey &&
        selectedProducts &&
        selectedProducts.length > 0
      ) {
        // Skip if we've already processed these products
        const selectedProductsKey = `context-${JSON.stringify(
          selectedProducts.map((p) => p._id),
        )}`;
        if (lastProcessedSource.current === selectedProductsKey) {
          return;
        }

        setLoading(true);
        setLoadError(null);
        try {
          // Fetch full product details to ensure we have all specifications
          const productsToCompare =
            await fetchFullProductDetails(selectedProducts);
          setProducts(productsToCompare);
          lastProcessedSource.current = selectedProductsKey;

          // Fetch category fields
          const categoryId =
            productsToCompare[0]?.category?._id ||
            productsToCompare[0]?.category ||
            (typeof productsToCompare[0]?.category === "object"
              ? productsToCompare[0]?.category?._id
              : null);

          if (categoryId) {
            try {
              const categoryDetails = await fetchCategoryDetails(categoryId);
              setCategoryFields(
                categoryDetails.allFields || categoryDetails.fields || [],
              );
            } catch (err) {
              console.error("Failed to fetch category fields:", err);
            }
          }
        } catch (err) {
          console.error("Error loading products from context:", err);
          showToast("Failed to load products", "error");
          setLoadError("We couldn't load these products. Please try again.");
        } finally {
          setLoading(false);
        }
      }
    };

    loadProducts();
  }, [
    location.state,
    selectedProducts,
    addProduct,
    clearProducts,
    showToast,
    reloadKey,
  ]);

  // Icon lookup
  const ICONS = { ...FaIcons, ...AiIcons, ...BsIcons };
  const IconByName = ({ name }) => {
    if (!name || typeof name !== "string") return null;
    const Icon = ICONS[name.trim()];
    return Icon ? <Icon className={styles.fieldIcon} /> : null;
  };

  const getNumericPrice = (product) => {
    const rawPrice = product?.price;
    const parsedPrice =
      typeof rawPrice === "string" ? Number(rawPrice) : rawPrice;
    return Number.isFinite(parsedPrice) ? parsedPrice : null;
  };

  const formatPrice = (product) => {
    const price = getNumericPrice(product);
    return price === null ? "—" : `$${price.toLocaleString()}`;
  };

  // Get all unique field names from all products
  const allFields = useMemo(() => {
    if (!categoryFields.length || !products.length) return [];

    const fieldMap = new Map();
    categoryFields.forEach((field) => {
      fieldMap.set(field.name, {
        name: field.name,
        label: field.label || field.name,
        icon: field.icon,
        type: field.type,
        unit: field.unit,
      });
    });

    return Array.from(fieldMap.values());
  }, [categoryFields, products]);

  // Get raw field value for a product (used for comparison + formatting)
  const getRawFieldValue = (product, fieldName) => {
    if (!product || !fieldName) return null;

    const fieldNameLower = fieldName.toLowerCase();
    let value = null;

    // 1. Use getSpecsMap to read from specifications first, then fallback to old fields
    const specsMap = getSpecsMap(product);

    // Try case-insensitive match
    const specsKeys = Object.keys(specsMap);
    const matchingKey = specsKeys.find(
      (key) => key.toLowerCase() === fieldNameLower,
    );
    if (matchingKey) {
      value = specsMap[matchingKey];
    }

    // 2. Check specifications array (if it exists)
    if (!value && Array.isArray(product.specifications)) {
      const spec = product.specifications.find((s) => {
        const specKey = (s.key || s.label || s.name || "").toLowerCase();
        return specKey === fieldNameLower;
      });
      if (
        spec &&
        spec.value !== null &&
        spec.value !== undefined &&
        spec.value !== ""
      ) {
        value = spec.value;
      }
    }

    // 3. Check direct product properties (case-insensitive)
    if (!value) {
      const productKeys = Object.keys(product);
      const matchingProductKey = productKeys.find(
        (key) => key.toLowerCase() === fieldNameLower,
      );
      if (matchingProductKey) {
        value = product[matchingProductKey];
      }
    }

    // 4. Special handling for common field names
    if (!value) {
      if (fieldNameLower === "brand") {
        value = product.brand?.name || product.brand;
      } else if (fieldNameLower === "model") {
        value = product.model;
      } else if (fieldNameLower === "year") {
        value = product.year;
      } else if (fieldNameLower === "price") {
        value = product.price;
      } else if (fieldNameLower === "rating") {
        value = product.rating;
      }
    }

    if (value !== null && value !== undefined && value !== "") {
      return value;
    }
    return null;
  };

  const getFieldComparison = useMemo(
    () => computeFieldComparisons(products, allFields, getRawFieldValue),
    [products, allFields],
  );

  // Calculate detailed metrics for each product
  const productMetrics = useMemo(() => {
    if (products.length < 2 || allFields.length === 0) return {};

    const metrics = {};

    products.forEach((product) => {
      let featureCount = 0;
      let numericFeatureSum = 0;
      let numericFeatureCount = 0;

      allFields.forEach((field) => {
        const rawValue = getRawFieldValue(product, field.name);
        if (rawValue !== null && rawValue !== undefined && rawValue !== "") {
          featureCount++;

          const numeric = parseNumericValue(rawValue);
          if (numeric !== null) {
            numericFeatureSum += numeric;
            numericFeatureCount++;
          }
        }
      });

      const avgFeatureValue =
        numericFeatureCount > 0 ? numericFeatureSum / numericFeatureCount : 0;

      const price = product.price || 0;

      const pricePerFeature = featureCount > 0 ? price / featureCount : 0;

      metrics[product._id] = {
        featureCount,
        avgFeatureValue,
        pricePerFeature,
        price,
      };
    });

    return metrics;
  }, [products, allFields]);

  // Value pick uses the same logic as the home comparison section.
  const searchParams = useMemo(
    () =>
      resolveSearchParamsForProducts(
        products,
        location.state?.searchParams ?? aiSession?.searchParams ?? null,
      ),
    [products, location.state?.searchParams, aiSession],
  );

  const comparisonTable = useMemo(
    () =>
      buildQuickComparisonTable({
        products,
        categories: getDefaultCategories(searchParams),
        searchParams,
      }),
    [products, searchParams],
  );

  const comparisonPicks = useMemo(
    () => getComparisonPicks(products, searchParams),
    [products, searchParams],
  );

  const topMatchProduct = useMemo(() => {
    if (comparisonPicks.topMatchIndex < 0) return null;
    return products[comparisonPicks.topMatchIndex] || null;
  }, [comparisonPicks.topMatchIndex, products]);

  const valuePickProduct = useMemo(() => {
    if (comparisonPicks.valuePickIndex < 0) return null;
    return products[comparisonPicks.valuePickIndex] || null;
  }, [comparisonPicks.valuePickIndex, products]);

  // Find cheapest and most expensive
  const priceRange = useMemo(() => {
    if (products.length < 2) {
      return { cheapest: null, mostExpensive: null, priceDiff: null };
    }

    const pricedProducts = products
      .map((product) => ({
        id: product._id,
        price: getNumericPrice(product),
      }))
      .filter((product) => product.price !== null);

    if (pricedProducts.length < 2) {
      return { cheapest: null, mostExpensive: null, priceDiff: null };
    }

    const cheapest = pricedProducts.reduce((min, p) =>
      p.price < min.price ? p : min,
    );
    const mostExpensive = pricedProducts.reduce((max, p) =>
      p.price > max.price ? p : max,
    );

    return {
      cheapest: cheapest.id,
      mostExpensive: mostExpensive.id,
      priceDiff: mostExpensive.price - cheapest.price,
    };
  }, [products]);

  const handleRemoveProduct = (e, productId) => {
    e.preventDefault();
    e.stopPropagation();

    // Update local state first (immediate visual feedback)
    setProducts((prev) => {
      const updated = prev.filter((p) => p._id !== productId);
      // If less than 2 products left, redirect back
      if (updated.length < 2) {
        setTimeout(() => {
          navigate(-1);
        }, 300);
      }
      return updated;
    });

    // Remove from context (sync with global state)
    removeProduct(productId);
  };

  if (loading) {
    return (
      <div className="pageContainer">
        <div className="innerContainer">
          <div className={styles.emptyState}>
            <h2>Loading comparison...</h2>
            <p>Please wait while we load the products.</p>
          </div>
        </div>
      </div>
    );
  }

  if (products.length < 2) {
    return (
      <div className="pageContainer">
        <div className="innerContainer">
          <div className={styles.emptyState} role={loadError ? "alert" : "status"}>
            <h2>
              {loadError
                ? "Couldn't load comparison"
                : "Not enough products to compare"}
            </h2>
            <p>
              {loadError ||
                "Select at least 2 products, then open Compare from the tray."}
            </p>
            <div className={styles.emptyActions}>
              {loadError && (
                <button
                  type="button"
                  className="buttonPrimary"
                  onClick={() => {
                    lastProcessedSource.current = null;
                    setReloadKey((key) => key + 1);
                  }}
                >
                  Retry
                </button>
              )}
              <button
                type="button"
                className="buttonSecondary"
                onClick={() => navigate(-1)}
              >
                <FaArrowLeft /> Go Back
              </button>
              <Link to="/" className="buttonPrimary">
                Browse categories
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pageContainer">
      <PageSeo
        title="Compare Products"
        description="Compare products side by side to make the smartest choice."
        pathname="/compare"
        noIndex
      />
      <div className="innerContainer">
        <div className={styles.comparisonPage}>
          <div className={styles.header}>
            <div className={styles.headerContent}>
              <div className={styles.headerTop}>
                <div>
                  <h2>
                    Comparing {products.length} Products
                  </h2>
                  <p className={styles.subtitle}>
                    Select products to add or remove from comparison
                  </p>
                </div>
                <div className={styles.headerActions}>
                  <button
                    type="button"
                    className={styles.clearBtn}
                    onClick={() => {
                      clearProducts();
                      navigate(-1);
                    }}
                  >
                    Clear All
                  </button>
                </div>
              </div>
            </div>
          </div>

          <ComparisonAiSummary
            products={products}
            aiSession={aiSession}
            searchParams={searchParams}
          />

          <div className={styles.sections}>
            <section className={styles.sectionCard}>
              <h3 className={styles.sectionTitle}>Products</h3>
              <div className={styles.productCards}>
              {products.map((product) => {
                const isTopMatch = topMatchProduct?._id === product._id;
                const isValuePick = valuePickProduct?._id === product._id;
                const isCheapest = priceRange.cheapest === product._id;
                const matchPercent = getMatchPercent(product);
                const rating = product.averageRating ?? product.rating;
                const isTopRated =
                  rating > 0 &&
                  rating ===
                    Math.max(
                      ...products.map((p) => p.averageRating ?? p.rating ?? 0),
                    );
                const displayName =
                  capitalizeFirst(product.name) ||
                  `${capitalizeFirst(
                    product.brand?.name || product.brand,
                  )} ${capitalizeFirst(product.model)}`;

                return (
                  <article
                    key={product._id}
                    className={`${styles.productCard} ${
                      isValuePick ? styles.bestValue : ""
                    } ${isTopMatch ? styles.topMatch : ""}`}
                  >
                    <div className={styles.productImage}>
                      <img
                        src={getProductImageUrl(product.images?.[0])}
                        alt={product.name}
                      />
                      <div className={styles.cardBadgeStack}>
                        {isTopMatch && (
                          <span className={styles.topMatchBadge}>
                            Top match
                          </span>
                        )}
                        {isValuePick && comparisonPicks.isValuePick && (
                          <span className={styles.bestValueBadge}>
                            <FaTrophy aria-hidden="true" /> Best value
                          </span>
                        )}
                        {isCheapest && !isValuePick && (
                          <span className={styles.lowestPriceBadge}>
                            Lowest price
                          </span>
                        )}
                      </div>
                      <button
                        className={`buttonRemove ${styles.removeBtn}`}
                        onClick={(e) => handleRemoveProduct(e, product._id)}
                        title="Remove from comparison"
                        type="button"
                        aria-label={`Remove ${displayName} from comparison`}
                      >
                        <FaTimes />
                      </button>
                    </div>
                    <h3 className={styles.productName}>{displayName}</h3>
                    <div className={styles.productPrice}>
                      {formatPrice(product)}
                    </div>
                    <div className={styles.cardMetaRow}>
                      {matchPercent > 0 && (
                        <span
                          className={`${styles.metaTag} ${
                            isTopMatch ? styles.metaTagMint : styles.metaTagPurple
                          }`}
                        >
                          {formatFitScoreBadge(matchPercent)}
                        </span>
                      )}
                      {rating > 0 && (
                        <span className={styles.productRating}>
                          <FaStar aria-hidden="true" />
                          <span>{rating}</span>
                        </span>
                      )}
                      {isTopRated && (
                        <span className={`${styles.metaTag} ${styles.metaTagAmber}`}>
                          Top rated
                        </span>
                      )}
                    </div>
                    <Link
                      to={`/product/${product._id}`}
                      className={styles.viewProductBtn}
                    >
                      View product
                      <FaChevronRight aria-hidden="true" />
                    </Link>
                  </article>
                );
              })}
              </div>
            </section>

            {allFields.length > 0 && (
              <section className={styles.sectionCard}>
                <h3 className={styles.sectionTitle}>Feature Comparison</h3>
                <div className={styles.tableWrapper}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th className={styles.featureCol}>Feature</th>
                        {products.map((product) => (
                          <th key={product._id} className={styles.productCol}>
                            {capitalizeFirst(product.name) ||
                              `${capitalizeFirst(
                                product.brand?.name || product.brand,
                              )} ${capitalizeFirst(product.model)}`}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {allFields.map((field) => {
                        const comparison = getFieldComparison[field.name];

                        return (
                          <tr key={field.name} className={styles.comparisonRow}>
                            <td className={styles.featureCol}>
                              <div className={styles.featureName}>
                                {field.icon && <IconByName name={field.icon} />}
                                <span>{capitalizeFirst(field.label)}</span>
                              </div>
                            </td>
                            {products.map((product) => {
                              const rawValue = getRawFieldValue(
                                product,
                                field.name,
                              );
                              const value = formatCompareFieldValue(
                                rawValue,
                                field,
                              );
                              const showColorSwatch =
                                rawValue !== null &&
                                rawValue !== undefined &&
                                rawValue !== "" &&
                                (field?.type === "color" ||
                                  extractCssColors(rawValue).length > 0);
                              const isBest = isComparisonBest(
                                comparison,
                                product._id,
                              );
                              const isWorst =
                                isComparisonWorst(comparison, product._id) &&
                                (comparison?.type === "boolean" ||
                                  products.length > 2);
                              const isSame = comparison?.allSame;

                              return (
                                <td
                                  key={product._id}
                                  className={`${styles.productCol} ${
                                    isBest ? styles.bestValue : ""
                                  } ${isWorst ? styles.worstValue : ""} ${
                                    isSame ? styles.sameValue : ""
                                  }`}
                                >
                                  <div className={styles.valueWrapper}>
                                    {isBest && (
                                      <FaCheckCircle
                                        className={styles.checkIcon}
                                        title="Best in this row"
                                      />
                                    )}
                                    {isWorst && (
                                      <FaMinusCircle
                                        className={styles.minusIcon}
                                        title="Lower value"
                                      />
                                    )}
                                    <span>
                                      {showColorSwatch
                                        ? renderSpecValue(rawValue, {
                                            styles,
                                            type: field?.type,
                                          })
                                        : value}
                                    </span>
                                  </div>
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </section>
            )}

            {products.length >= 2 && (
              <section className={styles.sectionCard}>
                <h3 className={styles.sectionTitle}>Quick Comparison Summary</h3>
                <div className={styles.metricsGrid}>
                  {products.map((product) => {
                    const metrics = productMetrics[product._id] || {};
                    const isCheapest = priceRange.cheapest === product._id;
                    const isMostExpensive =
                      priceRange.mostExpensive === product._id;

                    return (
                      <div key={product._id} className={styles.metricCard}>
                        <h4 className={styles.metricCardTitle}>
                          {capitalizeFirst(product.name) ||
                            `${capitalizeFirst(
                              product.brand?.name || product.brand,
                            )} ${capitalizeFirst(product.model)}`}
                        </h4>
                        <div className={styles.metricItems}>
                          <div className={styles.metricItem}>
                            <span className={styles.metricLabel}>Price:</span>
                            <span
                              className={`${styles.metricValue} ${
                                isCheapest ? styles.cheapest : ""
                              } ${isMostExpensive ? styles.mostExpensive : ""}`}
                            >
                              {formatPrice(product)}
                              {isCheapest && (
                                <span className={styles.metricBadge}>
                                  Cheapest
                                </span>
                              )}
                              {isMostExpensive && (
                                <span className={styles.metricBadge}>
                                  Most Expensive
                                </span>
                              )}
                            </span>
                          </div>
                          <div className={styles.metricItem}>
                            <span className={styles.metricLabel}>
                              Features:
                            </span>
                            <span className={styles.metricValue}>
                              {metrics.featureCount || 0}
                            </span>
                          </div>
                          <div className={styles.metricItem}>
                            <span className={styles.metricLabel}>
                              Price per Feature:
                            </span>
                            <span className={styles.metricValue}>
                              {Number.isFinite(metrics.pricePerFeature)
                                ? `$${metrics.pricePerFeature.toFixed(0)}`
                                : "—"}
                            </span>
                          </div>
                          {(product.averageRating ?? product.rating) > 0 && (
                            <div className={styles.metricItem}>
                              <span className={styles.metricLabel}>
                                Rating:
                              </span>
                              <span className={styles.metricValue}>
                                <FaStar color="#ffc107" size={14} />{" "}
                                {product.averageRating ?? product.rating}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                {priceRange.priceDiff !== null && priceRange.priceDiff > 0 && (
                  <div className={styles.priceDifference}>
                    <span className={styles.priceDiffLabel}>
                      Price Difference:
                    </span>
                    <span className={styles.priceDiffValue}>
                      ${priceRange.priceDiff.toLocaleString()}
                    </span>
                  </div>
                )}
              </section>
            )}
          </div>

          <ComparisonAskChat
            products={products}
            aiSession={aiSession}
            searchParams={searchParams}
            comparisonTable={comparisonTable}
            expanded={compareChatExpanded}
            onExpandedChange={setCompareChatExpanded}
            prefillPrompt={compareChatPrefill}
            onPrefillConsumed={() => setCompareChatPrefill(null)}
            onSuggestedPrompt={(prompt) => setCompareChatPrefill(prompt)}
          />
          <AppFeedbackPrompt
            source="compare"
            prompt="How is the compare tool working for you?"
            metadata={{
              productCount: products.length,
              sessionId: readHomeChatMatchSessionId(),
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default ComparisonPage;
